import type { OpportunityRoomAsset } from '~/hooks/apis/offers/useGetOffer';
import type {
  OpportunityRelatedDeal,
  OpportunityRelatedExtraGuestSurcharge,
} from '~/hooks/apis/offers/useGetOpportunityRelated';

import type { ChecklistStatus, MarginBand, RecordLike } from '../../shared/checklistUtils';
import {
  getOccupancyPaxFromText,
  getStatusLabel,
  isFiniteNumber,
  isPositiveNumber,
  mapOpportunityDealsByPackageId,
  normalizeString,
  readNumber,
  readString,
} from '../../shared/checklistUtils';

import type { EgsCapacityChecklistRow, EgsMarginChecklistRow } from './types';

const EGS_MARGIN_FIELD = 'Extra Guest Surcharges margin';
const EGS_CAPACITY_FIELD = 'EGS capacity';

export function calculateMarginPercentage(costPrice?: number, sellPrice?: number) {
  if (!isPositiveNumber(sellPrice) || !isFiniteNumber(costPrice)) {
    return undefined;
  }

  return ((sellPrice - costPrice) / sellPrice) * 100;
}

export function getMarginBand(margin?: number): MarginBand | undefined {
  if (!isFiniteNumber(margin)) {
    return undefined;
  }

  if (margin <= 10) {
    return 'red';
  }

  if (margin >= 19) {
    return 'green';
  }

  return 'yellow';
}

export function getMarginStatus(marginBand?: MarginBand): ChecklistStatus {
  if (!marginBand) {
    return 'requires approval';
  }

  return marginBand === 'green' ? 'approved' : 'requires approval';
}

export function buildEgsMarginRows(
  relatedExtraGuestSurcharges: Array<OpportunityRelatedExtraGuestSurcharge> = [],
): Array<EgsMarginChecklistRow> {
  return relatedExtraGuestSurcharges.map((relatedSurcharge) => {
    const costPrice =
      readNumber(relatedSurcharge, ['converted_cost__c']) ?? readNumber(relatedSurcharge, ['cost_price__c']);
    const sellPrice = readNumber(relatedSurcharge, ['sell_price_aud__c']);
    const sourceMargin = readNumber(relatedSurcharge, ['margin__c']);
    const margin = sourceMargin ?? calculateMarginPercentage(costPrice, sellPrice);
    const marginBand = getMarginBand(margin);
    const status = getMarginStatus(marginBand);

    return {
      ageRange: normalizeString(relatedSurcharge?.age_range__c),
      beddingType: normalizeString(relatedSurcharge?.other_extra_bedding_type__c || relatedSurcharge?.bedding_type__c),
      collectedBy: normalizeString(relatedSurcharge?.collected_by__c),
      id: `margin:extra_guest_surcharge__c:${String(relatedSurcharge.id)}`,
      costPrice,
      currencyCode: 'AUD',
      extraGuestType: normalizeString(relatedSurcharge?.extra_guest_type__c),
      field: EGS_MARGIN_FIELD,
      inclusions: normalizeString(relatedSurcharge?.inclusions__c),
      margin,
      marginBand,
      name: normalizeString(relatedSurcharge?.name) ?? String(relatedSurcharge.id),
      opportunityName: normalizeString(relatedSurcharge?.opportunity__c),
      sellPrice,
      status,
      statusLabel: getStatusLabel(status),
    };
  });
}

export function buildEgsCapacityRows({
  hasEgsMarginRows,
  packages,
  relatedDeals,
  relatedExtraGuestSurcharges,
}: {
  hasEgsMarginRows?: boolean;
  packages?: Array<App.Package>;
  relatedDeals?: Array<OpportunityRelatedDeal>;
  relatedExtraGuestSurcharges?: Array<OpportunityRelatedExtraGuestSurcharge>;
}): Array<EgsCapacityChecklistRow> {
  const hasConfiguredEgsMarginRows = hasEgsMarginRows ?? (relatedExtraGuestSurcharges?.length ?? 0) > 0;

  if (!hasConfiguredEgsMarginRows) {
    return [];
  }

  const relatedDealsByPackageId = mapOpportunityDealsByPackageId(relatedDeals);

  return (packages ?? []).map((offerPackage, index) => {
    const packageRecord = offerPackage as unknown as RecordLike;
    const roomTypeId = readString(packageRecord, ['fk_room_type_id']);
    const roomRateId = readString(packageRecord, ['fk_room_rate_id']);
    const salesforceId = readString(packageRecord, ['id_salesforce_external']);
    const relatedDeal = salesforceId ? relatedDealsByPackageId.get(salesforceId) : undefined;
    const dealName = offerPackage.deal_option_name || offerPackage.name;
    const includedCapacity = getIncludedCapacityFromSalesforce(relatedDeal) ?? getOccupancyPaxFromText(dealName);
    const maxRoomCapacity = getMaxRoomCapacityFromSalesforce(relatedDeal?.room_asset);
    const egsCapacity =
      isFiniteNumber(includedCapacity) && isFiniteNumber(maxRoomCapacity)
        ? maxRoomCapacity - includedCapacity
        : undefined;
    const status =
      isFiniteNumber(egsCapacity) && egsCapacity >= 0 && isPositiveNumber(includedCapacity)
        ? 'approved'
        : 'requires approval';

    return {
      egsCapacity,
      field: EGS_CAPACITY_FIELD,
      id: `capacity:package:${String(
        offerPackage.id_salesforce_external ?? offerPackage.le_package_id ?? index,
      )}:room_type:${roomTypeId ?? ''}:room_rate:${roomRateId ?? ''}`,
      includedCapacity,
      maxRoomCapacity,
      name: offerPackage.deal_option_name || offerPackage.name || `Package ${index + 1}`,
      roomRateId,
      roomName: normalizeString(relatedDeal?.room_asset?.name) || 'Room not mapped',
      roomTypeId,
      status,
      statusLabel: getStatusLabel(status),
    };
  });
}

function sumGuestFields(record: RecordLike, fields: [string, string, string]) {
  const [adultsField, childrenField, teenagersField] = fields;
  const adults = readNumber(record, [adultsField]);
  const children = readNumber(record, [childrenField]);
  const teenagers = readNumber(record, [teenagersField]);

  if (!isFiniteNumber(adults) && !isFiniteNumber(children) && !isFiniteNumber(teenagers)) {
    return undefined;
  }

  return (adults ?? 0) + (children ?? 0) + (teenagers ?? 0);
}

function getMaxRoomCapacityFromSalesforce(roomAsset?: OpportunityRoomAsset | null) {
  if (!roomAsset) {
    return undefined;
  }

  const record = roomAsset as unknown as RecordLike;
  const fromOccupancyText = getOccupancyPaxFromText(readString(record, ['max_occupancy__c']));

  if (isPositiveNumber(fromOccupancyText)) {
    return fromOccupancyText;
  }

  const explicitTotal = readNumber(record, ['maximum_occupancy_number__c']);

  if (isPositiveNumber(explicitTotal)) {
    return explicitTotal;
  }

  const fromGuestFields = sumGuestFields(record, ['max_adults__c', 'max_children__c', 'max_teenagers__c']);

  return isPositiveNumber(fromGuestFields) ? fromGuestFields : undefined;
}

function getIncludedCapacityFromSalesforce(relatedDeal?: OpportunityRelatedDeal) {
  if (!relatedDeal) {
    return undefined;
  }

  const dealMaxPax = readNumber(relatedDeal, ['max_room_pax__c']);

  if (isPositiveNumber(dealMaxPax)) {
    return dealMaxPax;
  }

  const fromGuestFields = sumGuestFields(relatedDeal, [
    'included_number_of_adults__c',
    'included_number_of_children__c',
    'included_number_of_teenagers__c',
  ]);

  return isPositiveNumber(fromGuestFields) ? fromGuestFields : undefined;
}
